import { useMemo } from 'react'

import { useIntl } from '@helpers/FormattedMessage'
import { useTaxExemptionRequirements } from '@queries/exemptionRequirements'

type Payload = {
  country: string
  excludeNoExemptionsProvinces?: boolean
}

export type Output = Omit<
  ReturnType<typeof useTaxExemptionRequirements>,
  'data'
> & {
  data: { label: string; value: string }[]
  canExcludePostalCode?: boolean
}

export const useProvinceFormatting = (country?: string) => {
  const { data, isLoading, isError } = useTaxExemptionRequirements({
    country: country || '',
  })
  const { formatMessage } = useIntl()

  return useMemo(() => {
    let errorOrLoadingMessage: string | undefined = undefined
    if (isError) {
      errorOrLoadingMessage = formatMessage({ id: 'errorFetchingContent' })
    } else if (isLoading) {
      errorOrLoadingMessage = formatMessage({ id: 'loading' })
    }

    /**
     * Given a province code (and country code from the closure), produce the
     * disaplay name of a province, e.g. US, CA => "California". Returns a
     * tuple, the second value indicates if the province name was successfully
     * retrieved. If it was the first value will be the province name. If not
     * the first value will be a message suitable for presentation indicating
     * that either the request to fetch the data is in flight or that there
     * was an error with that request.
     */
    const provinceCodeToName = (provinceCode: string): [string, boolean] => {
      if (errorOrLoadingMessage) {
        return [errorOrLoadingMessage, false]
      }

      const provinceName = data?.regions?.[provinceCode]?.name

      if (!provinceName) {
        return [formatMessage({ id: 'na' }), false]
      }

      return [provinceName, true]
    }

    /**
     * Lookup the combined Country + State/Province/Region name from the Country and Region codes.
     * For countries with named states/provinces, this will be "{CountryCode}, {ProvinceName}".
     * For countries without named regions, this will be "{CountryName}"
     */
    const provinceCodeToLocationName = (provinceCode?: string): string => {
      if (provinceCode) {
        const [msg, ready] = provinceCodeToName(provinceCode)

        return ready ? `${country}, ${msg}` : msg
      }
      if (errorOrLoadingMessage) {
        return errorOrLoadingMessage
      }
      const countryName = data?.name
      if (!countryName) {
        // This should never happen, since the query should either be loading or error out, causing
        // an errorOrLoadingMessage to be returned above before this happens
        throw new Error(`Country code "${country}" has no associated name`)
      }
      return countryName
    }

    return {
      provinceCodeToName,
      provinceCodeToLocationName,
    }
  }, [data, isLoading, isError, country, formatMessage])
}
export const useProvinceOptions = (payload: Payload): Output => {
  const { country, excludeNoExemptionsProvinces } = payload
  const { data, ...query } = useTaxExemptionRequirements({ country })

  const provinceOptions = useMemo(() => {
    if (!data?.regions) {
      return []
    }

    const options = Object.entries(data.regions).map(
      ([code, { name, status }]) => ({
        value: code,
        label: name,
        status,
      })
    )
    // Conditionally apply the filter based on excludeNoExemptionProvinces
    const filteredOptions = excludeNoExemptionsProvinces
      ? options.filter((option) => option.status !== 'NO_EXEMPTIONS')
      : options

    if (!filteredOptions.length) {
      return []
    }

    return filteredOptions
  }, [data, excludeNoExemptionsProvinces])

  return {
    data: provinceOptions,
    canExcludePostalCode: data?.canExcludePostalCode,
    ...query,
  }
}
